import type { RefreshToken, Tokens } from "@okta/okta-auth-js";
import type { AuthConfig } from "@urql/exchange-auth";
import { useLogger } from "@/composables/logger";
import { useAuth } from "@okta/okta-vue";
import { useOnline } from "@vueuse/core";
import { refreshAuthTokens } from "@/utils/authentication";

const isOnline = useOnline();

const { info } = useLogger("URQL_AUTH_EXCHANGE");

export const urqlAuthConfig: AuthConfig<Tokens> = {
  async getAuth({ authState }) {
    const authClient = useAuth();

    try {
      // if not online just get tokens from the auth client
      if (!isOnline.value) {
        return authClient.tokenManager?.getTokensSync() || authState;
      }

      // if we're not authenticated, throw an error so it gets handled and redirects
      if (!(await authClient.isAuthenticated({ onExpiredToken: "renew" }))) {
        throw new Error(`Auth client is not authenticated.`);
      }

      // renew and set tokens again.
      const tokens = await refreshAuthTokens(authClient);
      info("Access Tokens Refetched", tokens);

      // return the refreshed tokens
      return tokens;
    } catch (e: any) {
      await authClient.signInWithRedirect();
      return null;
    }
  },
  willAuthError({ authState }) {
    if (!authState?.accessToken?.accessToken) {
      return true;
    }
    return useAuth().tokenManager.hasExpired(authState.accessToken);
  },
  didAuthError({ error }) {
    return !!error.message;
  },
  addAuthToOperation({ authState, operation }) {
    // the token isn't in the auth state, return the operation without changes
    if (!authState?.accessToken?.accessToken) {
      return operation;
    }

    // fetchOptions can be a function (See Client API) but you can simplify this based on usage
    const fetchOptions =
      typeof operation.context.fetchOptions === "function"
        ? operation.context.fetchOptions()
        : operation.context.fetchOptions || {};

    return {
      ...operation,
      context: {
        ...operation.context,
        fetchOptions: {
          ...fetchOptions,
          headers: {
            ...fetchOptions.headers,
            Authorization: `Bearer ${authState.accessToken?.accessToken || ""}`
          }
        }
      }
    };
  }
};
